import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ToastContent } from "../../../shared/components";
import { useDebounce, useToast } from "../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { PermissionServices, IListagemPermissao } from "../../../shared/services/api/permissions/PermissionService";
import { returnMessageError } from "../../../shared/utils/returnMessageError";
import { checkByProfileGuard, ProfileGuard } from "../../../shared/guards/ProfileGuard";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";

import { TablePermissions } from "./TablePermissions";
import { ToolbarPermissions } from "./ToolbarPermissions";
import { clientTypeService } from "../../../shared/services/api/clientType/clientTypeService";

const ListPermissionsPresentation: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { debounce } = useDebounce();
	const toastHandler = useToast();

	const [rows, setRows] = useState<IListagemPermissao[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [totalCount, setTotalCount] = useState(0);
	const [profileOptions, setProfileOptions] = useState<Array<{ value: string; label: string }>>([]);
	const [isLoadingClientTypes, setIsLoadingClientTypes] = useState(false);
	const [clientTypesInitialized, setClientTypesInitialized] = useState(false);
	const [profileLabelById, setProfileLabelById] = useState<Record<string, string>>({});
	const [clientTypesAll, setClientTypesAll] = useState<any[]>([]);

	const hasViewPermission = useMemo(() => {
		return checkByProfileGuard(PermissionRolesEnum.PermissionsOptions, PermissionActionsEnum.View);
	}, []);

	const canFilter = useMemo(() => {
		return checkByProfileGuard(PermissionRolesEnum.PermissionsOptions, PermissionActionsEnum.Filter);
	}, []);

	const canAddPermission = useMemo(() => {
		return checkByProfileGuard(PermissionRolesEnum.PermissionsOptions, PermissionActionsEnum.Add);
	}, []);

	const busca = useMemo(() => {
		return searchParams.get("busca") || "";
	}, [searchParams]);

	const [buscaDraft, setBuscaDraft] = useState("");

	useEffect(() => {
		setBuscaDraft(busca);
	}, [busca]);

	const pagina = useMemo(() => {
		return Number(searchParams.get("pagina") || "1");
	}, [searchParams]);

	const rowsPerPage = useMemo(() => {
		return Number(searchParams.get("rowsPerPage") || "10");
	}, [searchParams]);

	const profileValue = useMemo(() => {
		return searchParams.get("perfil") || "Todos";
	}, [searchParams]);
	const [profileDraft, setProfileDraft] = useState("Todos");
	useEffect(() => {
		setProfileDraft(profileValue);
	}, [profileValue]);

	const statusValue = useMemo(() => {
		return searchParams.get("status") || "Todos";
	}, [searchParams]);
	const [statusDraft, setStatusDraft] = useState("Todos");
	useEffect(() => {
		setStatusDraft(statusValue);
	}, [statusValue]);

	useEffect(() => {
		const paramsObj = Object.fromEntries(searchParams.entries());
		const nextParams = {
			busca: paramsObj.busca ?? "",
			pagina: paramsObj.pagina ?? "1",
			rowsPerPage: paramsObj.rowsPerPage ?? "10",
			perfil: paramsObj.perfil ?? "Todos",
			status: paramsObj.status ?? "Todos",
		};

		const isAlreadyNormalized =
			paramsObj.busca === nextParams.busca &&
			paramsObj.pagina === nextParams.pagina &&
			paramsObj.rowsPerPage === nextParams.rowsPerPage &&
			paramsObj.perfil === nextParams.perfil &&
			paramsObj.status === nextParams.status;
		if (isAlreadyNormalized) return;

		setSearchParams(nextParams, { replace: true });
	}, [searchParams, setSearchParams]);

	const paramsReady = useMemo(() => {
		return (
			searchParams.has("pagina") &&
			searchParams.has("rowsPerPage") &&
			searchParams.has("perfil") &&
			searchParams.has("status") &&
			searchParams.has("busca")
		);
	}, [searchParams]);

	const updateParams = (next: { busca?: string; pagina?: string; rowsPerPage?: string; perfil?: string; status?: string }) => {
		setSearchParams(
			{
				busca,
				pagina: String(pagina),
				rowsPerPage: String(rowsPerPage),
				perfil: profileValue,
				status: statusValue,
				...next,
			},
			{ replace: true }
		);
	};

	const clientTypeIdForQuery = useMemo(() => {
		if (profileValue === "Todos") return "";
		if (!clientTypesInitialized) return null;
		const selected = clientTypesAll.find((ct) => String(ct?.id) === String(profileValue) || String(ct?.clientTypeId) === String(profileValue));
		return String(selected?.clientTypeId ?? profileValue);
	}, [profileValue, clientTypesAll, clientTypesInitialized]);

	useEffect(() => {
		if (!hasViewPermission) return;
		if (!paramsReady) return;
		if (clientTypeIdForQuery === null) return;

		setIsLoading(true);
		debounce(() => {
			const statusToSend = statusValue === "Todos" ? "" : statusValue;
			PermissionServices.getAll(pagina, busca, statusToSend, "", "", rowsPerPage, clientTypeIdForQuery ?? "")
				.then((result) => {
					if (result instanceof Error) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title="Alerta!!" content={`Erro: ${result.message}`} />,
						});
						setRows([]);
						setTotalCount(0);
						return;
					}

					setRows(result.data);
					setTotalCount(result.totalCount);
				})
				.catch((err: any) => {
					const { titleMessage, contentMessage } = returnMessageError(err);
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				})
				.finally(() => setIsLoading(false));
		});
	}, [busca, pagina, rowsPerPage, hasViewPermission, statusValue, clientTypeIdForQuery, paramsReady]);

	useEffect(() => {
		if (!hasViewPermission) return;
		let isMounted = true;
		setClientTypesInitialized(false);

		const loadClientTypes = async () => {
			setIsLoadingClientTypes(true);
			try {
				const res = await clientTypeService.getAll(1, "", 9999);
				const all: any[] = res?.data ?? [];

				if (!isMounted) return;

				setClientTypesAll(all);

				const uniqueByKey = new Map<string, { value: string; label: string }>();
				for (const ct of all) {
					const value = String(ct.id ?? "").trim();
					if (!value) continue;
					uniqueByKey.set(value, {
						value,
						label: `${ct.description} (${ct.level})`,
					});
				}
				setProfileOptions(Array.from(uniqueByKey.values()));

				const map: Record<string, string> = {};
				for (const ct of all) {
					const keyByClientTypeId = String(ct.clientTypeId ?? "").toLowerCase();
					const keyById = String(ct.id ?? "").toLowerCase();
					if (keyByClientTypeId) map[keyByClientTypeId] = ct.description;
					if (keyById) map[keyById] = ct.description;
				}
				setProfileLabelById(map);
			} catch (err: any) {
				if (!isMounted) return;
				const { titleMessage, contentMessage } = returnMessageError(err);
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
				});
				setProfileOptions([]);
				setClientTypesAll([]);
				setProfileLabelById({});
			} finally {
				if (!isMounted) return;
				setIsLoadingClientTypes(false);
				setClientTypesInitialized(true);
			}
		};

		loadClientTypes();
		return () => {
			isMounted = false;
		};
	}, [hasViewPermission]);

	return (
		<LayoutBaseDePagina
			titulo="Permissões"
			subtitulo="Gerenciamento de Permissões"
			barraDeFerramentas={
				<ToolbarPermissions
					busca={buscaDraft}
					showSearch={canFilter}
					onChangeBusca={setBuscaDraft}
					onSearch={() =>
						updateParams({
							pagina: "1",
							busca: buscaDraft,
							perfil: profileDraft,
							status: statusDraft,
						})
					}
					onClearSearch={() => {
						setBuscaDraft("");
					}}
					profileValue={profileDraft}
					showInputProfile={canFilter}
					onChangeProfile={(perfil) => setProfileDraft(perfil)}
					profileOptions={profileOptions}
					statusValue={statusDraft}
					showInputStatus={canFilter}
					onChangeStatus={(status) => setStatusDraft(status)}
					textoButtonNew="novo"
					showButtonNew={hasViewPermission}
					isButtonNewDisabled={!canAddPermission}
					onClickNovo={() => navigate("/permissoes/detalhe/novo")}
				/>
			}
		>
			<ProfileGuard requiredRole={PermissionRolesEnum.PermissionsOptions} requiredAction={PermissionActionsEnum.View}>
				<TablePermissions
					rows={rows}
					isLoading={isLoading}
					totalCount={totalCount}
					pagina={pagina}
					rowsPerPage={rowsPerPage}
					profileLabelById={profileLabelById}
					onChangePage={(newPage) => updateParams({ pagina: newPage.toString() })}
					onChangeRowsPerPage={(newRowsPerPage) => updateParams({ pagina: "1", rowsPerPage: newRowsPerPage.toString() })}
					onEdit={(permissionId) => navigate(`/permissoes/detalhe/${permissionId}`)}
				/>
			</ProfileGuard>
		</LayoutBaseDePagina>
	);
};

export default ListPermissionsPresentation;